import type { PageSection } from '@sshop/domain';
import { z } from 'zod';

import { parseDto } from '../../mappers/parseDto';
import type { Transport } from '../../transport/Transport';

import { pageSectionListDto, type PageSectionDto } from './pageSection.dto';

const cmsPageDto = z.object({ data: z.object({
  title: z.string(), description: z.string(), description_2: z.string(),
  is_custom: z.boolean().default(false), display_layout: z.enum(['standard', 'wide']).default('standard'),
  meta_title: z.string(), meta_description: z.string(),
  meta_keyword: z.string().default(''), meta_og_title: z.string().default(''),
  meta_og_description: z.string().default(''), image_url: z.string().nullable().default(null),
}) });

const homeLayoutDto = z.object({ data: z.array(z.object({
  key: z.string(), visible: z.boolean(),
  title: z.string().default(''), subtitle: z.string().default(''),
})) });

export interface HomePageLayoutEntry {
  readonly key: string;
  readonly visible: boolean;
  readonly title: string;
  readonly subtitle: string;
}

export interface PageSectionService {
  navigation(signal?: AbortSignal): Promise<readonly CmsNavigationEntry[]>;
  list(pageKey: string, signal?: AbortSignal): Promise<readonly PageSection[]>;
  homeLayout(signal?: AbortSignal): Promise<readonly HomePageLayoutEntry[]>;
  get(pageKey: string, signal?: AbortSignal): Promise<{
    title: string;
    isCustom: boolean;
    displayLayout: 'standard' | 'wide';
    description: string;
    description2: string;
    metaTitle: string;
    metaDescription: string;
    metaKeywords: string;
    metaOgTitle: string;
    metaOgDescription: string;
    imageUrl: string | null;
  }>;
}

export interface CmsNavigationEntry {
  readonly key: string;
  readonly title: string;
  readonly url: string;
  readonly header: boolean;
  readonly footer: boolean;
}

const navigationDto = z.object({data: z.array(z.object({
  key: z.string(), title: z.string(), url: z.string().regex(/^\/(ge|en)\/pages\/[a-z0-9]+(?:-[a-z0-9]+)*$/),
  header: z.boolean(), footer: z.boolean(),
}))});

function toSection(dto: PageSectionDto): PageSection {
  return {
    id: dto.id,
    key: dto.key,
    title: dto.title,
    text: dto.text,
    tag: dto.tag,
    ctaLabel: dto.cta_label,
    link: dto.link,
    bgColor: dto.bg_color,
    placement: dto.placement,
    layout: dto.layout,
    rowKey: dto.row_key,
    showInCategoriesMenu: dto.show_in_categories_menu,
    categoriesMenuOrder: dto.categories_menu_order,
    sortOrder: dto.sort_order,
    image: dto.image,
  };
}

export function createLaravelPageSectionService(transport: Transport): PageSectionService {
  return {
    async navigation(signal) {
      const res = await transport.request({method: 'GET', path: '/pages/navigation', ...(signal !== undefined ? { signal } : {})});
      return parseDto(navigationDto, res.data, 'GET /pages/navigation').data;
    },
    async homeLayout(signal) {
      const res = await transport.request({
        method: 'GET', path: '/pages/home/layout',
        ...(signal !== undefined ? { signal } : {}),
      });
      return parseDto(homeLayoutDto, res.data, 'GET /pages/home/layout').data;
    },
    async get(pageKey, signal) {
      const res = await transport.request({
        method: 'GET', path: `/pages/${encodeURIComponent(pageKey)}`,
        ...(signal !== undefined ? { signal } : {}),
      });
      const data = parseDto(cmsPageDto, res.data, `GET /pages/${pageKey}`);
      return {
        title: data.data.title, description: data.data.description,
        isCustom: data.data.is_custom, displayLayout: data.data.display_layout,
        description2: data.data.description_2,
        metaTitle: data.data.meta_title, metaDescription: data.data.meta_description,
        metaKeywords: data.data.meta_keyword,
        metaOgTitle: data.data.meta_og_title,
        metaOgDescription: data.data.meta_og_description,
        imageUrl: data.data.image_url,
      };
    },
    async list(pageKey: string, signal): Promise<readonly PageSection[]> {
      const res = await transport.request({
        method: 'GET',
        path: `/pages/${encodeURIComponent(pageKey)}/sections`,
        ...(signal !== undefined ? { signal } : {}),
      });
      const parsed = parseDto(pageSectionListDto, res.data, `GET /pages/${pageKey}/sections`);
      return parsed.data.map(toSection);
    },
  };
}
